import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { WordmarkStacked } from '@/components/brand/Logo';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { ShieldCheck, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';

// Curated set of presenter-only demonstration accounts. Individual role
// accounts (manager / field / viewer) and the isolation-test HREL org
// remain in the database for internal testing but are intentionally
// omitted from the login page — they are not relevant to senior officials
// evaluating the product.
const QUICK = [
  { role: 'Platform Super Administrator', email: 'superadmin@aakashdrishti.com', password: 'SuperAdmin@1234' },
  { role: 'Organisation Administrator (demo)', email: 'admin@aakash.com', password: 'Demo@1234' },
  { role: 'Remote-Sensing Analyst (demo)', email: 'analyst@aakash.com', password: 'Demo@1234' },
];

export default function LoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [initialised, setInitialised] = useState(true); // assume yes until we know otherwise
  usePageTitle('Secure Login');

  useEffect(() => {
    document.documentElement.classList.add('dark');
    if (user) nav('/app/dashboard', { replace: true });
    // Enable presenter shortcut: ?presenter=1 shows demo panel
    const p = new URLSearchParams(loc.search);
    if (p.get('presenter') === '1') setShowDemo(true);
    // Check whether the platform has been initialised on this environment.
    // Silently ignore any network error — the auth submit will surface a
    // meaningful error if the backend is unreachable.
    api.get('/public/bootstrap-status')
      .then((r) => setInitialised(!!r.data?.initialized))
      .catch(() => { /* silent */ });
  }, [user, nav, loc]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      await login(email, password);
      const from = loc.state?.from || '/app/dashboard';
      nav(from, { replace: true });
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Login failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen app-shell-dark flex items-center justify-center p-4">
      <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-8 items-center relative z-10">
        <div className="hidden lg:block">
          <div className="max-w-md">
            <WordmarkStacked />
            <div className="mt-8">
              <h1 className="font-display text-3xl font-semibold">Command centre for critical infrastructure.</h1>
              <p className="mt-3 text-muted-foreground">Sign in to monitor your assets, review AI-validated satellite observations, and manage the operational workflow end-to-end.</p>
              <div className="mt-6 flex items-start gap-2 text-xs text-muted-foreground border border-border rounded-lg p-3 bg-secondary/40">
                <ShieldCheck size={14} className="text-primary mt-0.5 shrink-0" />
                <span>Access is restricted to authorised users. All login attempts and activities may be recorded for security and audit purposes.</span>
              </div>
            </div>
          </div>
        </div>
        <Card className="p-6 border-border">
          <div className="lg:hidden mb-6 flex justify-center"><WordmarkStacked /></div>
          <h2 className="text-lg font-semibold">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-1">Enter your organisational credentials.</p>

          {!initialised && (
            <Alert variant="destructive" className="mt-4" data-testid="login-not-initialised">
              <AlertTriangle size={14} />
              <AlertDescription>
                This environment has not been initialised yet. Please wait a moment and refresh, or contact your administrator.
              </AlertDescription>
            </Alert>
          )}

          {err && <Alert variant="destructive" className="mt-4" data-testid="login-error"><AlertDescription>{err}</AlertDescription></Alert>}

          <form onSubmit={submit} className="mt-4 space-y-3" data-testid="login-form">
            <div>
              <Label htmlFor="email">Organisation email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@organisation.gov.in" data-testid="login-email-input" autoComplete="email" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" autoComplete="current-password" />
            </div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="login-form-submit-button">
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
            <div className="text-center text-xs">
              <a className="text-muted-foreground hover:text-primary">Forgot password?</a>
            </div>
          </form>

          <div className="mt-4 lg:hidden text-[11px] flex items-start gap-2 text-muted-foreground border border-border rounded-md p-2 bg-secondary/40">
            <ShieldCheck size={12} className="text-primary mt-0.5 shrink-0" />
            <span>Access is restricted to authorised users. Activities may be recorded for audit.</span>
          </div>

          <div className="mt-5 pt-4 border-t border-border">
            <button type="button" className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-primary"
                    data-testid="toggle-demo-accounts" onClick={() => setShowDemo(v => !v)}>
              {showDemo ? <EyeOff size={12} /> : <Eye size={12} />}
              {showDemo ? 'Hide' : 'Show'} demonstration accounts
            </button>
            {showDemo && (
              <div className="mt-2 grid grid-cols-1 gap-1">
                {QUICK.map((q) => (
                  <button
                    type="button"
                    key={q.email}
                    data-testid={`quick-login-${q.email}`}
                    className="text-left text-xs rounded-md px-2 py-1 hover:bg-secondary flex items-center justify-between gap-2"
                    onClick={() => { setEmail(q.email); setPassword(q.password); }}
                  >
                    <span className="truncate">{q.role}</span>
                    <span className="font-mono text-muted-foreground shrink-0">{q.email}</span>
                  </button>
                ))}
                <div className="text-[10px] text-muted-foreground mt-1">Click a role above to auto-fill credentials. Demonstration environment only.</div>
              </div>
            )}
          </div>

          <div className="mt-4 text-center text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">← Return to website</Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
